import { VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';
import { buttonVariants } from '@/ui/shared/buttonVariants';

type AllowTags = 'button' | 'a';

type IconPosition = 'left' | 'right';
type IconProps =
  | {
      icon: ReactNode;
      iconPosition: IconPosition;
    }
  | {
      icon?: undefined;
      iconPosition?: undefined;
    };

type Props<E extends AllowTags = 'button'> = React.ComponentProps<E> &
  VariantProps<typeof buttonVariants> &
  IconProps & {
    as?: E;
  };

export const Button = <E extends AllowTags = 'button'>({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon,
  iconPosition,
  children,
  as: propsAs,
  ...props
}: Props<E>) => {
  const className = buttonVariants({ variant, size, fullWidth });
  const as = propsAs || 'button';

  if (as === 'button') {
    const { type, ...buttonProps } = props as React.ComponentProps<'button'>;
    const buttonType = type ?? 'button';

    return (
      <button type={buttonType} className={className} {...buttonProps}>
        {icon && iconPosition === 'left' && icon}
        {children}
        {icon && iconPosition === 'right' && icon}
      </button>
    );
  }

  // as === 'a'
  const anchorProps = props as React.ComponentProps<'a'>;

  return (
    <a className={className} {...anchorProps}>
      {icon && iconPosition === 'left' && icon}
      {children}
      {icon && iconPosition === 'right' && icon}
    </a>
  );
};
